import { Placeholder, Text } from '@lmn/design-system';
import styled, { css } from 'styled-components';
import { IconCross } from '@/app/common/icons/libraryIcons';
import type { AirlineSetting, AirlineValues } from '@lmn/lmn-commonlogic/dist/config/airlines/airlineTypes';
import { PriceWithTextContainer } from '@/app/components/hotel-detail/changeTransport/Price';
import { useEffect } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import GroupLuggageMessage from '@/app/components/hotel-detail/flight-train/GroupLuggageMessage';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { getCheckedBaggageText, getLuggageTranslation } from '@/app/components/hotel-detail/common/luggageTranslations';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { useTranslation } from '../../translations/Translation';
import { getCompaniesLogos } from './utils';
import { IconCheck } from '../common/icons';
import { useAirlineSettings } from '../hooks/useAirlineSettings';
import { StyledIconCoins } from './BrandedFaresCommon';

// #region styled components

const FareContainerTitle = styled.div`
  padding-bottom: 12px;
`;

export const FareContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  padding: 16px 16px 12px;
  gap: 16px;
`;

const Fare = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
`;

const ServiceContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
  padding-top: 12px;
`;

const Service = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  span {
    padding: 4px 0;
  }
`;

const StyledIconCross = styled(IconCross)`
  color: ${(p) => p.theme.commons.colors.gray400};
  max-height: 16px;
  max-width: 16px;
`;

// #endregion

type DefaultBrandedFareProps = {
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  numberOfBaggagePiecesIncluded: number | null;
  hideTitle?: boolean;
  onLoadFare: () => void;
} & React.HTMLAttributes<HTMLDivElement>;

const defaultAirlineSettings: AirlineSetting = {
  seatSelection: false,
  priorityBoarding: false,
  cabinBag: false,
  brandedFares: false,
  basicFare: '',
};

export function DefaultBrandedFare(props: DefaultBrandedFareProps) {
  const { outbound, inbound, numberOfBaggagePiecesIncluded, hideTitle, onLoadFare, ...rest } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { adults, children } = useUrlSearchParams();
  const { data: airlineSettingsResponse, isLoading } = useAirlineSettings();
  const services = getAirlineConfigs({ outbound, inbound, airlineConfigs: airlineSettingsResponse?.airlineSettings });
  const numberOfLuggage = (adults ?? 0) + (children ?? 0);
  const groupLuggagePieces = numberOfBaggagePiecesIncluded ?? 0;
  const showGroupLuggage = groupLuggagePieces > 0;

  useEffect(() => {
    if (!isLoading) {
      onLoadFare();
    }
  }, [isLoading, onLoadFare]);

  return (
    <section {...rest}>
      {hideTitle ? null : (
        <FareContainerTitle>
          <Text variant="Title4">{t('vacation.select.flight_detail_modal.included_fare')}</Text>
        </FareContainerTitle>
      )}
      {isLoading || !airlineSettingsResponse?.airlineSettings ? (
        <FareCardSkeleton />
      ) : (
        <FareContainer>
          <Fare>
            {services.basicFare !== '' ? (
              <Text variant="Title5">{toCamelCase(services.basicFare!)}</Text>
            ) : (
              <Text variant="Title5">{t('vacation.select.flight_detail_modal.cabin')}</Text>
            )}
          </Fare>

          <ServiceContainer>
            <Service>
              <IconCheck />
              <Text variant="BodySmall" forceTag="span">
                {getLuggageTranslation('hand', numberOfLuggage, t)}
              </Text>
            </Service>
            {outbound.handBaggageIncluded && inbound.handBaggageIncluded ? (
              <Service>
                <IconCheck />
                <Text variant="BodySmall" forceTag="span">
                  {getLuggageTranslation('cabin', numberOfLuggage, t)}
                </Text>
              </Service>
            ) : (
              services.cabinBag && (
                <Service>
                  <StyledIconCoins />
                  <Text variant="BodySmall" forceTag="span">
                    {t('vacation.select.flight_detail_modal.hand_luggage_add_later')}
                  </Text>
                </Service>
              )
            )}
            {(outbound.includeBaggage && inbound.includeBaggage) || showGroupLuggage ? (
              <Service>
                <IconCheck />
                <Text variant="BodySmall" forceTag="span">
                  {getCheckedBaggageText({
                    showGroupLuggage,
                    numberOfBaggagePiecesIncluded: groupLuggagePieces,
                    numberOfLuggage,
                    t,
                  })}
                </Text>
              </Service>
            ) : inbound.baggageStartingPrice > 0 || outbound.baggageStartingPrice > 0 ? (
              <Service>
                <StyledIconCoins />
                <Text variant="BodySmall" forceTag="span">
                  {t('vacation.select.flight_detail_modal.checked_bag_add_later', {
                    amount: `${formatPrice({
                      price: Math.min(
                        inbound.baggageStartingPrice > 0 ? inbound.baggageStartingPrice : outbound.baggageStartingPrice,
                        outbound.baggageStartingPrice > 0 ? outbound.baggageStartingPrice : inbound.baggageStartingPrice
                      ),
                      localization,
                    })}`,
                  })}
                </Text>
              </Service>
            ) : null}
            {services.seatSelection && (
              <Service>
                <StyledIconCoins />
                <Text variant="BodySmall" forceTag="span">
                  {t('vacation.select.flight_detail_modal.seat_add_later')}
                </Text>
              </Service>
            )}
            {services.priorityBoarding && (
              <Service>
                <StyledIconCoins />
                <Text variant="BodySmall" forceTag="span">
                  {t('vacation.select.flight_detail_modal.priority_add_later')}
                </Text>
              </Service>
            )}
            <Service>
              <StyledIconCross />
              <Text variant="BodySmall" forceTag="span">
                {t('vacation.select.flight_detail_modal.flight_cancellation_policy')}
              </Text>
            </Service>
          </ServiceContainer>
          {showGroupLuggage && <GroupLuggageMessage groupLuggagePieces={groupLuggagePieces} />}
        </FareContainer>
      )}
    </section>
  );
}

function getAirlineConfigs({
  inbound,
  outbound,
  airlineConfigs,
}: {
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  airlineConfigs?: AirlineValues;
}) {
  if (!airlineConfigs) {
    return defaultAirlineSettings;
  }

  const transportAirlines = getCompaniesLogos([...outbound.segments, ...inbound.segments]);
  const configuredAirlines = new Set(Object.keys(airlineConfigs));
  const transportArlineConfigs = transportAirlines
    .filter((transportAirline) => configuredAirlines.has(transportAirline))
    .map((transportAirline) => airlineConfigs[transportAirline]);
  return transportArlineConfigs.length === transportAirlines.length
    ? transportArlineConfigs.reduce(
        (acc, config) => ({
          seatSelection: !!config.seatSelection && acc.seatSelection,
          priorityBoarding: !!config.priorityBoarding && acc.priorityBoarding,
          cabinBag: !!config.cabinBag && acc.cabinBag,
          brandedFares: !!config.brandedFares && acc.brandedFares,
          basicFare:
            config.basicFare && config.basicFare !== '' && config.basicFare === acc.basicFare ? acc.basicFare : '',
        }),
        {
          seatSelection: true,
          priorityBoarding: true,
          cabinBag: true,
          brandedFares: true,
          basicFare: transportArlineConfigs[0].basicFare,
        }
      )
    : defaultAirlineSettings;
}

function toCamelCase(str: string): string {
  const lowerCase = str.toLowerCase();
  return `${lowerCase.charAt(0).toUpperCase()}${lowerCase.slice(1)}`;
}

export const FareCardSkeleton = () => (
  <section>
    <FareContainer>
      <Fare
        css={css`
          align-items: center;
        `}
      >
        <Placeholder height="20px" width="170px" />
        <PriceContainer>
          <PriceWithTextContainer>
            <Placeholder height="26px" width="74px" />
          </PriceWithTextContainer>
        </PriceContainer>
      </Fare>

      <ServiceContainer
        css={`
          gap: 8px;
        `}
      >
        <Placeholder height="20px" width="196px" />
        <Placeholder height="20px" width="173px" />
        <Placeholder height="20px" width="237px" />
        <Placeholder height="20px" width="214px" />
        <Placeholder height="20px" width="211px" />
        <Placeholder height="20px" width="211px" />
      </ServiceContainer>
    </FareContainer>
  </section>
);
